import { isAxiosError } from 'axios';
import type { AnyValueMap } from '@opentelemetry/api-logs';

/**
 * Safely serializes any thrown value into an AnyValueMap suitable for OTel log bodies.
 *
 * WHY THIS EXISTS
 * JavaScript Error objects have non-enumerable prototype properties (message,
 * stack, name). Spreading with { ...error } or JSON.stringify produces {} for
 * plain Errors and most subclasses (including AxiosError). This function reads
 * those properties explicitly so they are always present in the log record.
 *
 * USAGE
 *   emitErrorLog({ body: { message: 'Something failed', error: serializeError(e) } })
 *
 * DO NOT use { ...error } or JSON.stringify(error) anywhere in the OTel pipeline.
 */
export function serializeError(error: unknown): AnyValueMap {
  if (error == null) return {};

  if (typeof error === 'string') return { message: error };

  // AxiosError extends Error — handle it first to capture HTTP context.
  // All properties here are explicitly read because spread of Error subclasses
  // produces {} due to non-enumerable props on Error.prototype.
  if (isAxiosError(error)) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
      // HTTP context — the most actionable fields for debugging API failures.
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      method: error.config?.method,
      // JSON-stringify the response body: it may contain field-level errors
      // (e.g. { email: ['already exists'] }) and the body is unknown-typed,
      // so we flatten it to a string to satisfy AnyValueMap constraints.
      responseData:
        error.response?.data != null
          ? JSON.stringify(error.response.data)
          : undefined,
    };
  }

  // Plain Error or any subclass (TypeError, ReferenceError, etc.).
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
    };
  }

  // Plain object: coerce each value to a scalar so the result satisfies AnyValueMap.
  // Unknown-typed or complex values are JSON-stringified rather than dropped.
  if (typeof error === 'object') {
    const entries = Object.entries(error as Record<string, unknown>).map(
      ([k, v]) => {
        if (
          v == null ||
          typeof v === 'string' ||
          typeof v === 'number' ||
          typeof v === 'boolean'
        ) {
          return [k, v] as [string, AnyValueMap[string]];
        }
        return [k, JSON.stringify(v)] as [string, AnyValueMap[string]];
      },
    );
    return Object.fromEntries(entries);
  }

  // Primitive fallback (number, boolean, symbol, etc.).
  return { value: String(error) };
}
